import {Component, EventEmitter, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {SelectedFinishProfileItem} from "../../profile/interface/selected-finish-profile-item";
import {CustomSize} from "../../cart/interface/custom-size";
import {SharedTransmissionService} from "../../raintree/transmission/shared-transmission.service";
import {CountryList} from "../../address/interface/countryList";
import {ToastMessageService} from "../../services/toast-message.service";
import {FormControl} from "@angular/forms";
import {IReview} from "../interface/review";
import {ReviewTransmissionService} from "../../raintree/transmission/review-transmission.service";
import {RaintreeResponse} from "../../raintree/interface/raintree-response";
import {OrderedItem} from "../../profile/interface/ordered-item";
import {GlobalService} from "../../services/global.service";
import ImageViewer from 'awesome-image-viewer'
import {ImageTransmissionService} from "../../raintree/transmission/upload-image/image-transmission.service";
import {Router} from "@angular/router";

@Component({
	selector: 'fb-create-review-dialog',
	templateUrl: './create-review-dialog.component.html',
	styleUrls: ['./create-review-dialog.component.scss']
})
export class CreateReviewDialogComponent implements OnInit {

	private existingReview: IReview = {} as IReview;

	protected stars: boolean[] = Array(5).fill(0);
	protected rating: number = 0;
	protected hoverRating: number = 0;
	protected ratingChange: EventEmitter<number> = new EventEmitter<number>();
	protected files: File[] = [];
	protected imagePreviews: string[] = [];
	protected countries: CountryList[] = [];

	protected city: FormControl = new FormControl("");
	protected country: FormControl = new FormControl("");
	protected description: FormControl = new FormControl("");

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: OrderedItem,
		public dialogRef: MatDialogRef<CreateReviewDialogComponent>,
		private _shared: SharedTransmissionService,
		private _toast: ToastMessageService,
		private _api: ReviewTransmissionService,
		private _global: GlobalService,
		private _image: ImageTransmissionService,
		private _router: Router
	) {
	}

	ngOnInit(): void {
		this._shared.fetchCountryList()
			.subscribe((response: CountryList[]) => {
				this.countries = response;
				this.patchReview();
			});
	}

	private enableForm(): void {
		this.city.enable();
		this.country.enable();
		this.description.enable();
	}

	private disableForm(): void {
		this.city.disable();
		this.country.disable();
		this.description.disable();
	}

	private patchReview(): void {
		if (!this.data.hasOwnProperty('reviewId')) return;

		this._api.getReview(
			this.data.reviewId || 0,
			() => {
				this.disableForm();
			},
			(response: IReview) => {
			},
			(response: IReview) => {
				this.existingReview = response;

				this.city.patchValue(response.city);
				this.country.patchValue(response.country);
				this.description.patchValue(response.description);
				this.rating = response.rating;
			},
			(error: string) => {
				this._toast.error("Unable to fetch review!", "Please try again later.")
				this.dialogRef.close();
			},
			() => {
				this.enableForm();
			}
		);
	}

	private async resolveImageUrls(): Promise<string[]> {
		let imageList: string[] = [];
		let uploadPromises: Promise<void>[] = [];

		Array.from(this.files).forEach((file: File) => {
			let image: FormData = new FormData();
			image.append('imageFile', file);

			let uploadPromise = new Promise<void>((resolve, reject) => {
				this._image.uploadImage(
					image,
					() => {
					},
					() => {
					},
					(response) => {
						imageList.push(response.imageUrl);
						resolve();
					},
					(error: string) => {
						this._toast.error("Unable to submit review!", "Please try again later.");
						reject(error);
					},
					() => {
						this.dialogRef.close();
					}
				);
			});

			uploadPromises.push(uploadPromise);
		});

		await Promise.all(uploadPromises);
		return imageList;
	}

	private async addReview(): Promise<void> {

		const payload: IReview = {
			name: this._global.customerProfileData.tenant.name,
			city: this.city.value.trim(),
			country: this.country.value.trim(),
			description: this.description.value.trim(),
			link: '',
			rating: this.rating,
			productId: this.resolveProductId(),
			orderItemId: this.data?.id || 0,
			activeUrl: this._router.url,
			adminAdded: false
		}

		if (this.files.length > 0) {
			try {
				const imageUrls: string[] = await this.resolveImageUrls();
				payload.productImages = imageUrls.join(',');
			} catch (error) {
				return; // Stop execution if image upload fails
			}
		}

		this._api.addReview(
			payload,
			() => {
			},
			(response) => {
			},
			(response: RaintreeResponse) => {
				this._toast.success("Review submitted successfully!", "Thank you");
				this.dialogRef.close();
			},
			(error) => {
				this._toast.error("Unable to submit review!", "Please try again later.");
			},
			() => {
			}
		);
	}

	private async updateReview(): Promise<void> {

		const payload: IReview = {
			...this.existingReview,
			name: this._global.customerProfileData.tenant.name,
			city: this.city.value.trim(),
			country: this.country.value.trim(),
			description: this.description.value.trim(),
			rating: this.rating
		}

		if (this.files.length > 0) {
			try {
				const imageUrls: string[] = await this.resolveImageUrls();
				payload.productImages = imageUrls.join(',');
			} catch (error) {
				return; // Stop execution if image upload fails
			}
		}

		this._api.updateReview(
			payload,
			() => {
			},
			(response) => {
			},
			(response: RaintreeResponse) => {
				this._toast.success("Review submitted successfully!", "Thank you");
				this.dialogRef.close();
			},
			(error) => {
				this._toast.error("Unable to submit review!", "Please try again later.");
			},
			() => {
				this.dialogRef.close();
			}
		);
	}

	private resolveProductId(): number {
		if (!this.data?.id) return 0;

		return (this.data.productGroup == 'fabric' || this.data.productGroup == 'swatch')
			? this.data.customization.fabricProductPreview!.product.id
			: this.data.customization.finishedProductPreview!.product.id;
	}

	protected rate(rating: number): void {
		this.rating = rating;
		this.ratingChange.emit(this.rating);
	}

	protected prepareFinishes(selectedFinishItems: SelectedFinishProfileItem[] | undefined): string {
		if (!selectedFinishItems || !selectedFinishItems.length) return '';

		return selectedFinishItems.map((item: SelectedFinishProfileItem) => item.label).join(", ")
	}

	protected prepareCustomSize(customSize: CustomSize | undefined): string {
		if (!customSize || !Object.keys(customSize).length) return '';

		return Object.entries(customSize).map((value) => `${value[0]}: ${value[1]}`).join(', ');
	}

	protected onSubmit(): void {

		if (this.rating < 1) {
			this._toast.error("Please provide rating!", "rating should be between 1 to 5");
			return;
		}

		if (!this.city.value.trim() || !this.country.value.trim()) {
			this._toast.error("Please fill up mandatory fields!", "Country is mandatory");
			return;
		}

		!this.existingReview.id ? this.addReview() : this.updateReview();
	}

	protected onImageClick(index: number): void {
		new ImageViewer({
			images: Array.of({mainUrl: this.imagePreviews[index]})
		})
	}

	protected onFileSelected(event: any): void {
		this.files = event.target.files;
		if (this.files) {
			this.imagePreviews = [];
			// @ts-ignore
			Array.from(this.files).forEach((file: File) => {
				const reader = new FileReader();
				reader.onload = (e: any) => {
					this.imagePreviews.push(e.target.result);
				};
				reader.readAsDataURL(file);
			});
		}
	}
}
